"use client";

import { ButtonProps } from "@/components/atoms/Button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/molecules/Popover";
import * as React from "react";
import { ComboboxFilter, ComboboxFilterProps } from "./ComboboxFilter";
import { ComboboxTrigger } from "./ComboboxTrigger";
import { ControllerRenderProps } from "react-hook-form";

export type ComboboxProps = {
  options: { value: string; label: string }[];
  renderTrigger?: (
    selectedOption: {
      value: string;
      label: string;
    },
    field?: ControllerRenderProps,
  ) => React.ReactNode;
  emptyMessage?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  value?: string | undefined;
  onValueChange?: (value: string | null) => void;
  triggerProps?: ButtonProps & { placeholder?: React.ReactNode };
  testId?: string;
  filterProps?: Omit<
    ComboboxFilterProps,
    "options" | "selectedValue" | "onSelectedItemChange"
  >;
};

export const Combobox = ({
  options,
  renderTrigger,
  emptyMessage = "No options found.",
  fullWidth,
  disabled,
  value,
  readOnly,
  onValueChange,
  triggerProps,
  filterProps,
  testId = "combobox",
}: ComboboxProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const selectedOption = (options || []).find(
    (option) => option.value === value,
  );

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild role="combobox" aria-expanded={isOpen}>
        {!!renderTrigger ? (
          renderTrigger(selectedOption as { value: string; label: string })
        ) : (
          <ComboboxTrigger
            testId={testId ? `${testId}-trigger` : undefined}
            fullWidth={fullWidth}
            disabled={disabled}
            value={value}
            readOnly={readOnly}
            {...(triggerProps || {})}
          >
            {selectedOption?.label || triggerProps?.placeholder}
          </ComboboxTrigger>
        )}
      </PopoverTrigger>
      <PopoverContent className="tw-min-w-[200px] tw-w-[--radix-popover-trigger-width] tw-p-0 tw-relative tw-z-max">
        <ComboboxFilter
          emptyMessage={emptyMessage}
          testId={testId ? `${testId}-filter` : undefined}
          options={options}
          selectedValue={value}
          onSelectedItemChange={({ selectedItem }) => {
            onValueChange?.(selectedItem ? selectedItem.value : null);
            setIsOpen(false);
          }}
          {...(filterProps || {})}
          onIsOpenChange={({ isOpen, ...rest }) => {
            setIsOpen(isOpen);
            filterProps?.onIsOpenChange?.({ isOpen, ...rest });
          }}
        />
      </PopoverContent>
    </Popover>
  );
};
